import { useTranslation } from "react-i18next";
import { useGetAllFedexAccounts } from "@/api/fedexAccount.api";
import { formatPrice } from "@/lib/money.util";
import { cn } from "@/lib/utils";
import {
  DATE_FORMAT,
  DATE_PLACEHOLDER,
  DATE_TIMELINE_FORMAT,
  formatDateSafe,
} from "@/lib/logFormat";
import { ExpeditionFedexApiDto } from "@/types/api/api-types";

type ExpeditionPreFinalizeDetailsProps = {
  details: unknown;
};

// Log details are untyped JSON; only narrow to an object, fields stay optional at render time.
const extractFedexApi = (details: unknown): Partial<ExpeditionFedexApiDto> | null => {
  if (!details || typeof details !== "object") return null;
  const fedexApi = (details as { expeditionFedexApi?: unknown }).expeditionFedexApi;
  if (!fedexApi || typeof fedexApi !== "object") return null;
  return fedexApi as Partial<ExpeditionFedexApiDto>;
};

const formatAmount = (amount: number | undefined, currency: string) => {
  if (typeof amount !== "number") return DATE_PLACEHOLDER;
  return currency === "EUR" ? formatPrice(amount) : `${amount} ${currency}`;
};

export const ExpeditionPreFinalizeDetails = ({ details }: ExpeditionPreFinalizeDetailsProps) => {
  const { t } = useTranslation(["boLogs", "common"]);
  const fedexApi = extractFedexApi(details);
  const { data: fedexAccounts } = useGetAllFedexAccounts();
  if (!fedexApi) return null;

  const fedexAccount = fedexAccounts?.find((account) => account.id === fedexApi.fedexAccountId);

  const rate = fedexApi.fedexRate;
  const currency = rate?.currency ?? "EUR";
  const priceRows = rate
    ? [
        { label: t("preFinalize.totalBaseCharge"), amount: rate.totalBaseCharge },
        { label: t("preFinalize.totalFreightDiscounts"), amount: rate.totalFreightDiscounts },
        { label: t("preFinalize.totalSurcharges"), amount: rate.totalSurcharges },
        { label: t("preFinalize.totalVatCharge"), amount: rate.totalVatCharge },
        { label: t("preFinalize.totalDutiesAndTaxes"), amount: rate.totalDutiesAndTaxes },
      ]
    : [];

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium text-gray-600">{t("preFinalize.fedexTitle")}</p>
      <div className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50 p-4">
        <DetailRow
          label={t("preFinalize.shipDate")}
          value={formatDateSafe(fedexApi.shipDate, DATE_TIMELINE_FORMAT)}
        />
        <DetailRow
          label={t("preFinalize.serviceType")}
          value={rate?.serviceName ?? fedexApi.serviceType ?? DATE_PLACEHOLDER}
        />
        <DetailRow
          label={t("preFinalize.deliveryDate")}
          value={formatDateSafe(rate?.deliveryDate, DATE_FORMAT)}
        />
        <DetailRow
          label={t("preFinalize.fedexAccountNumber")}
          value={fedexAccount?.accountNumber ?? DATE_PLACEHOLDER}
        />
        <DetailRow
          label={t("preFinalize.shippingReference")}
          value={fedexApi.shippingReference?.trim() || DATE_PLACEHOLDER}
        />
      </div>

      {rate && (
        <>
          <p className="mt-3 text-sm font-medium text-gray-600">{t("preFinalize.pricesTitle")}</p>
          <div className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50 p-4">
            {priceRows.map(({ label, amount }) => (
              <DetailRow key={label} label={label} value={formatAmount(amount, currency)} />
            ))}
            <DetailRow
              label={t("preFinalize.saturdayDelivery")}
              value={rate.saturdayDelivery ? t("common:boolean.yes") : t("common:boolean.no")}
            />
            <div className="mt-1 border-t border-gray-200 pt-2">
              <DetailRow
                label={t("preFinalize.totalNetCharge")}
                value={formatAmount(rate.totalNetCharge, currency)}
                strong
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

type DetailRowProps = {
  label: string;
  value: string;
  strong?: boolean;
};

const DetailRow = ({ label, value, strong }: DetailRowProps) => (
  <div className="flex items-start justify-between gap-4 text-sm">
    <span className={cn("flex-shrink-0 text-gray-600", strong ? "font-semibold" : "font-medium")}>
      {label}
    </span>
    <span
      className={cn(
        "min-w-0 text-end break-all text-gray-700",
        strong && "font-semibold text-gray-900",
      )}
    >
      {value}
    </span>
  </div>
);
